import { isCapacityIncomplete, type TrainingGpuCapacity } from "@/api";

// 여유 비율을 사용자가 취할 행동으로 변환한다: 그냥 쓴다 / 곧 빠듯하다 / 거의 없다 / 지금은 못 쓴다 /
// 잴 수 없다. 임계값과 표시 문구는 FE 가 소유한다. upstream 판정(status)은 쓰지 않는다.
export type GpuFreeTone = "ample" | "tight" | "scarce" | "exhausted" | "unknown";

// 절반 이상 비어 있으면 지금 제출해도 대개 바로 실행된다. 20% 아래이면 여러 명이 동시에 제출할 때
// 대기가 생기기 시작한다. 그래서 주의 구간으로 판정한다.
const ampleFreeRatio = 0.5;
const tightFreeRatio = 0.2;

const gpuFreeToneLabels: Readonly<Record<GpuFreeTone, string>> = {
  ample: "여유 있음",
  tight: "여유 적음",
  scarce: "여유 거의 없음",
  exhausted: "여유 없음",
  unknown: "여유를 알 수 없음",
};

// 여유 비율을 톤 구간으로 변환한다(경계 포함: ≥50% ample / ≥20% tight / >0 scarce / 0 exhausted).
// free_gpus 는 서버가 계산한 값을 그대로 나눈다. holder 점유를 다시 빼면 실제보다 나쁘게 판정한다.
// 전체가 0 이면 비율이 없다. 노드가 모두 내려간 경우와 값을 못 읽어 0 인 경우를 구분할 수 없으므로 unknown 이다.
// 대기 job 은 톤에 넣지 않는다. 여유가 남아 있는데 대기가 있는 상태도 빨강으로 표시하면,
// 여유 0 인 상태를 구분할 수 없다.
// errors 도 톤을 덮지 않는다. errors 는 대개 수집기 하나의 실패다. 매번 회색으로 덮으면 신호가
// 사라진다. 불완전함은 경고 아이콘과 툴팁 문장이 이미 표시한다.
export function gpuFreeTone(capacity: TrainingGpuCapacity): GpuFreeTone {
  if (capacity.total_allocatable_gpus <= 0) {
    return "unknown";
  }
  const ratio = capacity.free_gpus / capacity.total_allocatable_gpus;
  if (ratio >= ampleFreeRatio) {
    return "ample";
  }
  if (ratio >= tightFreeRatio) {
    return "tight";
  }
  return ratio > 0 ? "scarce" : "exhausted";
}

// 헤더 위젯의 한 줄이다. 대기는 k8s Workload 축(pending_jobs)만 표시한다. 0 이어도 공간을 유지해
// 숫자가 바뀔 때 헤더 레이아웃이 움직이지 않게 한다. 다른 축(pending_sessions)은 팝오버가 표시한다.
export function gpuWidgetLabel(capacity: TrainingGpuCapacity): string {
  return `GPU ${capacity.free_gpus}/${capacity.total_allocatable_gpus} · 대기 ${capacity.pending_jobs}`;
}

// 위젯 hover 툴팁이다. 축약된 숫자가 각각 무엇인지 문장으로 설명한다. 점 색만으로는 판별할 수
// 없으므로 같은 판정을 문구로 앞에 함께 표시한다. 값만 적는 상태 표시이므로 체언으로 끝낸다.
export function gpuWidgetTooltip(capacity: TrainingGpuCapacity): string {
  const tone = gpuFreeToneLabels[gpuFreeTone(capacity)];
  const summary = `${tone} · 쓸 수 있는 GPU ${capacity.free_gpus}개 · 대기 중인 작업 ${capacity.pending_jobs}개`;
  if (isCapacityIncomplete(capacity)) {
    return `${summary}. 일부 값을 읽지 못해 수치가 불완전합니다.`;
  }
  return summary;
}
